import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../stores/languageStore';
import { InformationCircleIcon, MapPinIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Lawsuit, SupportedCountry } from '@tvia-ai/shared';
import { FILE_UPLOAD_CONFIG, validateFileList, formatFileSize } from '../../utils/fileUpload';
import { useEmailVerification } from '../../hooks/useEmailVerification';
import EmailVerificationModal from '../auth/EmailVerificationModal';
import { useJurisdictionDetection } from '../../hooks/useJurisdictionDetection';
import { useAnalytics } from '../../hooks/useAnalytics';

interface CaseDetailsStepProps {
  data: Lawsuit;
  onUpdate: (data: Partial<Lawsuit>, uploadedFiles?: File[]) => void;
  onNext: () => void;
}

const COUNTRIES = [
  { code: 'US', name: 'United States', currencies: ['USD'] },
  { code: 'UK', name: 'United Kingdom', currencies: ['GBP'] },
  { code: 'IL', name: 'Israel', currencies: ['ILS'] },
  { code: 'AE', name: 'United Arab Emirates', currencies: ['AED'] },
  { code: 'DE', name: 'Germany', currencies: ['EUR'] },
  { code: 'FR', name: 'France', currencies: ['EUR'] },
];

const CaseDetailsStep: React.FC<CaseDetailsStepProps> = ({ data, onUpdate, onNext }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const { trackEvent } = useAnalytics();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isManualCountrySelection, setIsManualCountrySelection] = useState(false);

    // Helper function to get localized country name
  const getLocalizedCountryName = (countryCode: SupportedCountry | null, fallbackName: string): string => {
    if (!countryCode) return fallbackName;
    
    try {
      // Try to get the localized country name
      const localizedName = t(`countries.${countryCode}`);
      // If translation key doesn't exist, it returns the key itself, so check for that
      return localizedName.startsWith('countries.') ? fallbackName : localizedName;
    } catch {
      return fallbackName;
    }
  };
  
  // Email verification hook
  const {
    isModalOpen,
    openVerificationModal,
    closeVerificationModal,
    shouldPromptForVerification,
    userEmail,
  } = useEmailVerification();

  // Jurisdiction detection hook
  const {
    detectedJurisdiction,
    isLoading: isDetectingJurisdiction,
    error: detectionError,
    detectionResult,
    retryDetection,
    canRetry,
    confidenceLevel,
  } = useJurisdictionDetection(true);
  
  // Auto-update country when jurisdiction is detected
  useEffect(() => {
    if (detectedJurisdiction && !isManualCountrySelection) {
      // Always update to detected jurisdiction if not manually selected
      if (data.country !== detectedJurisdiction) {
        
        // Track auto-selection
        trackEvent('COUNTRY_SELECTION', {
          selected_country: detectedJurisdiction,
          selection_type: 'auto_detected',
          detected_country: detectedJurisdiction,
          overriding_detection: false,
          confidence: detectionResult?.confidence || 0,
          previous_country: data.country,
        });
        
        onUpdate({ country: detectedJurisdiction });
      }
    }
  }, [detectedJurisdiction, isManualCountrySelection, data.country, onUpdate, trackEvent, detectionResult]);

  // Handle successful verification
  const handleVerificationSuccess = () => {
    closeVerificationModal();
    // Proceed to next step after successful verification
    onNext();
  };

  const handleInputChange = (field: keyof Lawsuit, value: any) => {
    onUpdate({ [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCountryChange = (country: string) => {
    setIsManualCountrySelection(true); // Mark as manual selection
    
    // Track manual country selection
    trackEvent('COUNTRY_SELECTION', {
      selected_country: country,
      selection_type: 'manual',
      detected_country: detectedJurisdiction,
      overriding_detection: detectedJurisdiction !== country,
    });
    
    onUpdate({ country: country as SupportedCountry });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validationError = validateFileList(files, selectedFiles.length);
    if (validationError) {
      setFileError(validationError.message);
      return;
    }
    setFileError(null);
    const newFiles = [...selectedFiles, ...files];
    setSelectedFiles(newFiles);
    // Only pass uploadedFiles as a separate argument
    onUpdate({}, newFiles);
  };

  const removeFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    onUpdate({}, newFiles);
  };

  const isFormValid = (): boolean => {
    if (!data.userStory.trim() || data.userStory.length < 100) return false;
    return true;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!data.userStory.trim()) {
      newErrors.userStory = t('errors.userStoryRequired');
    } else if (data.userStory.length < 100) {
      newErrors.userStory = t('errors.userStoryTooShort', 'Your story must be at least 100 characters');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      // Check if email verification is required
      if (shouldPromptForVerification()) {
        openVerificationModal();
        return;
      }
      
      // If email is verified or not required, proceed
      onNext();
    }
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {t('filing.caseDetails.title')}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('filing.caseDetails.subtitle')}
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800">
                {t('filing.caseDetails.aiGeneratedNotice', 'Our AI will analyze your case description and automatically determine the case title, legal category, and estimated claim amount based on your story and additional questions.')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Your Story */}
      <div>
        <label htmlFor="userStory" className="block text-sm font-medium text-gray-700 mb-2">
          {t('filing.caseDetails.yourStory', 'Describe Your Case')} *
        </label>
        <textarea
          id="userStory"
          rows={10}
          value={data.userStory}
          onChange={(e) => handleInputChange('userStory', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.userStory ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={t('filing.caseDetails.yourStoryPlaceholder', 'Tell us your story in detail. What happened? When did it happen? Who was involved? What damages did you suffer? Include all relevant dates, locations, and any supporting details. The more information you provide, the better we can help you.')}
        />
        <div className="flex justify-between mt-1">
          {errors.userStory ? (
            <p className="text-sm text-red-600">{errors.userStory}</p>
          ) : (
            <p className="text-sm text-gray-500">
              {t('filing.caseDetails.provideDetailedInfo', 'Please provide detailed information about your case (minimum 100 characters)')}
            </p>
          )}
          <p className="text-sm text-gray-500">
            {data.userStory.length}/10000
          </p>
        </div>
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('filing.caseDetails.uploadFiles', 'Upload Supporting Files')}
        </label>
        <input
          type="file"
          multiple
          accept={FILE_UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none focus:border-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 mb-2"
        />
        {fileError && <p className="text-sm text-red-600 mb-2">{fileError}</p>}
        {selectedFiles.length > 0 && (
          <ul className="mb-2 space-y-2">
            {selectedFiles.map((file, idx) => (
              <li key={idx} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-gray-900 font-medium text-sm break-all">{file.name} ({formatFileSize(file.size)})</span>
                <button type="button" className="text-red-500 hover:text-red-700 text-sm font-medium self-start sm:self-center whitespace-nowrap px-2 py-1" onClick={() => removeFile(idx)}>
                  {t('filing.caseDetails.remove', 'Remove')}
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-gray-500">
          {t('filing.caseDetails.fileTypes', `PDF, Word, images, or text files up to ${FILE_UPLOAD_CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB each (max ${FILE_UPLOAD_CONFIG.MAX_FILES_PER_UPLOAD} files)`)}
        </p>
      </div>

      {/* Country Selection with Auto-Detection */}
      <div className="space-y-4">
        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
            {t('filing.caseDetails.country')} *
          </label>
          
          {/* Auto-detection status */}
          {isDetectingJurisdiction && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                <span className="text-sm text-blue-700">
                  {t('filing.caseDetails.detectingLocation', 'Detecting your location...')}
                </span>
              </div>
            </div>
          )}

          {/* Detection result */}
          {detectionResult && !isDetectingJurisdiction && (
            <div className={`mb-3 p-3 rounded-lg border ${
              confidenceLevel === 'high' ? 'bg-green-50 border-green-200' :
              confidenceLevel === 'medium' ? 'bg-yellow-50 border-yellow-200' :
              'bg-orange-50 border-orange-200'
            }`}>
              <div className="flex items-start gap-2">
                <MapPinIcon className={`h-4 w-4 mt-0.5 ${
                  confidenceLevel === 'high' ? 'text-green-600' :
                  confidenceLevel === 'medium' ? 'text-yellow-600' :
                  'text-orange-600'
                }`} />
                <div className="flex-1">
                  <div className={`text-sm font-medium ${
                    confidenceLevel === 'high' ? 'text-green-800' :
                    confidenceLevel === 'medium' ? 'text-yellow-800' :
                    'text-orange-800'
                  }`}>
                  {detectionResult.isSupported ? (
                    t('filing.caseDetails.jurisdictionDetected', 'Jurisdiction detected as {{country}}', {
                      country: getLocalizedCountryName(detectionResult.detectedCountry, detectionResult.countryName)
                    })
                  ) : (
                    t('filing.caseDetails.jurisdictionNotSupported', 'Location detected as {{country}} but not supported', {
                      country: detectionResult.countryName
                    })
                  )}
                  </div>
                  {detectionResult.fallbackApplied && (
                    <div className="text-xs text-orange-600 mt-1">
                      {t('filing.caseDetails.usingFallback', 'Using default jurisdiction due to detection limitations')}
                    </div>
                  )}
                  {isManualCountrySelection && (
                    <div className="text-xs text-gray-600 mt-1">
                      {t('filing.caseDetails.manualOverride', 'Manual selection active')}
                    </div>
                  )}
                </div>
                {canRetry && detectionError && (
                  <button
                    onClick={retryDetection}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    {t('filing.caseDetails.retry', 'Retry')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Detection error - only show if there's actually an error message */}
          {detectionError && !isDetectingJurisdiction && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-red-800">
                    {t('filing.caseDetails.detectionFailed', 'Auto-detection failed')}
                  </div>
                  <div className="text-xs text-red-600 mt-1">
                    {detectionError}
                  </div>
                  {canRetry && (
                    <button
                      onClick={retryDetection}
                      className="text-xs text-red-600 hover:text-red-800 underline mt-1"
                    >
                      {t('filing.caseDetails.retry', 'Retry')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <select
            id="country"
            value={data.country || 'US'}
            onChange={(e) => handleCountryChange(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-base min-h-[48px]"
          >
            {COUNTRIES.map(country => (
              <option key={country.code} value={country.code}>
                {t(`countries.${country.code}`)}
                {detectedJurisdiction === country.code && !isManualCountrySelection && ' 📍'}
              </option>
            ))}
          </select>
          
          <p className="text-xs text-gray-500 mt-1">
            {t('filing.caseDetails.countryHelp', 'We automatically detect your location for jurisdiction. You can change this selection if needed.')}
          </p>
        </div>
      </div>

      {/* Class Action Checkbox with Tooltip */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="relative mt-1">
            <input
              type="checkbox"
              id="isClassAction"
              checked={data.isClassAction}
              onChange={(e) => handleInputChange('isClassAction', e.target.checked)}
              className="sr-only"
            />
            <div
              className={`w-4 h-4 border-2 rounded cursor-pointer transition-all ${
                data.isClassAction
                  ? 'bg-blue-600 border-blue-600'
                  : 'bg-white border-gray-300 hover:border-blue-500'
              }`}
              onClick={() => handleInputChange('isClassAction', !data.isClassAction)}
            >
              {data.isClassAction && (
                <svg
                  className="w-3 h-3 text-white absolute top-0 left-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
          </div>
          <div className="ml-3 flex-1">
            <label 
              htmlFor="isClassAction" 
              className="flex items-center text-sm font-medium text-gray-700 cursor-pointer"
              onClick={() => handleInputChange('isClassAction', !data.isClassAction)}
            >
              {t('filing.caseDetails.classAction')}
              <div className="relative ml-2 group">
                <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10">
                  {t('filing.caseDetails.classActionTooltip', 'Class actions reduce your costs by sharing legal fees with other participants and increase your leverage against large organizations.')}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </label>
            <p className="mt-1 text-sm text-green-700">
              {t('filing.caseDetails.classActionBenefit', 'Recommended: Class actions reduce your individual costs and increase your chances of success by joining forces with others who have similar claims.')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleNext}
          disabled={!isFormValid()}
          className={`px-6 py-3 rounded-lg text-base font-medium min-h-[48px] transition-all ${
            isFormValid()
              ? 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {t('filing.caseDetails.nextStep')}
        </button>
      </div>
      
      {/* Email Verification Modal */}
      <EmailVerificationModal
        isOpen={isModalOpen}
        onClose={closeVerificationModal}
        onVerified={handleVerificationSuccess}
        userEmail={userEmail || ''}
      />
    </div>
  );
};

export default CaseDetailsStep; 
